import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, FileText, User, Mail, Phone, MapPin, Info, ShieldCheck, AlertCircle } from 'lucide-react';
import Button from '../components/Button';
import Section from '../components/Section';

const KVKKForm: React.FC = () => {
  const [formData, setFormData] = useState({
    adSoyad: '',
    tcKimlikNo: '',
    email: '',
    telefon: '',
    adres: '',
    basvuruTuru: '',
    aciklama: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Gerçek senaryoda burası bir API'ye istek atar.
    console.log('KVKK Form submitted:', formData);
    alert(
      'Başvurunuz alınmıştır! En kısa sürede (yasal süre içinde) size dönüş yapılacaktır.'
    );
    setFormData({
      adSoyad: '',
      tcKimlikNo: '',
      email: '',
      telefon: '',
      adres: '',
      basvuruTuru: '',
      aciklama: '',
    });
  };

  // Animasyon
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <>
      {/* 1. HERO SECTION: Koyu Tema ve Efektler */}
      <section className="relative pt-32 pb-24 bg-primary-900 overflow-hidden">
         {/* Arka Plan Işıkları */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute top-[-20%] right-[10%] w-[500px] h-[500px] bg-purple-600 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[400px] h-[400px] bg-teal-500 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
             <div className="inline-flex items-center justify-center w-20 h-20 bg-white/5 rounded-3xl mb-6 text-primary-300 border border-white/10 backdrop-blur-sm shadow-2xl">
               <FileText size={40} />
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 text-white tracking-tight">
              KVKK Başvuru Merkezi
            </h1>
            <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Kişisel verileriniz üzerindeki haklarınızı önemsiyoruz. <br className="hidden md:block" />
              6698 sayılı Kanun kapsamındaki taleplerinizi buradan güvenle iletebilirsiniz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. FORM ALANI */}
      <Section className="py-20 -mt-12 relative z-20">
        <motion.div
            className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl shadow-gray-900/10 border border-gray-100 overflow-hidden"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
        >
            {/* Bilgilendirme Header */}
            <div className="bg-gray-50 p-8 md:p-10 border-b border-gray-200">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="bg-white p-4 rounded-2xl shadow-sm text-primary-600 shrink-0">
                        <ShieldCheck size={32} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Başvuru Süreci Hakkında</h2>
                        <p className="text-gray-600 text-sm leading-relaxed">
                            Talebinizin niteliğine göre en geç <strong>30 gün</strong> içinde başvurularınız ücretsiz olarak sonuçlandırılacaktır. 
                            Ancak işlemin ayrıca bir maliyet gerektirmesi hâlinde, Kişisel Verileri Koruma Kurulunca belirlenen tarifedeki ücret alınabilir.
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 md:p-12 space-y-10">
                
                {/* GRUP 1: KİMLİK BİLGİLERİ */}
                <div>
                    <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100">
                        <div className="bg-primary-50 p-2 rounded-lg text-primary-600">
                            <User size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Başvuru Sahibi Bilgileri</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="group">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Ad Soyad <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="adSoyad"
                                value={formData.adSoyad}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                                placeholder="Adınız ve Soyadınız"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">T.C. Kimlik No <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="tcKimlikNo"
                                value={formData.tcKimlikNo}
                                onChange={handleChange}
                                required
                                maxLength={11}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                                placeholder="11 haneli kimlik numaranız"
                            />
                        </div>

                        <div className="md:col-span-2">
                           <label className="block text-sm font-medium text-gray-700 mb-2">Tebligat Adresi <span className="text-red-500">*</span></label>
                           <div className="relative">
                               <MapPin className="absolute top-3.5 left-4 text-gray-400" size={18} />
                               <textarea
                                   name="adres"
                                   value={formData.adres}
                                   onChange={handleChange}
                                   required
                                   rows={2}
                                   className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all resize-none"
                                   placeholder="Resmi tebligat adresiniz"
                               />
                           </div>
                        </div>
                    </div>
                </div>

                {/* GRUP 2: İLETİŞİM BİLGİLERİ */}
                <div>
                    <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100">
                        <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                            <Phone size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">İletişim Bilgileri</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">E-posta <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Mail className="absolute top-3.5 left-4 text-gray-400" size={18} />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                                    placeholder="ornek@email.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Telefon <span className="text-red-500">*</span></label>
                            <div className="relative">
                                <Phone className="absolute top-3.5 left-4 text-gray-400" size={18} />
                                <input
                                    type="tel"
                                    name="telefon"
                                    value={formData.telefon}
                                    onChange={handleChange}
                                    required
                                    className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                                    placeholder="05XX XXX XX XX"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* GRUP 3: TALEP DETAYLARI */}
                <div>
                    <div className="flex items-center gap-2 mb-6 pb-2 border-b border-gray-100">
                        <div className="bg-purple-50 p-2 rounded-lg text-purple-600">
                            <FileText size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Talep Detayları</h3>
                    </div>

                     <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Talep Konusu <span className="text-red-500">*</span></label>
                            <select
                                name="basvuruTuru"
                                value={formData.basvuruTuru}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all appearance-none"
                            >
                                <option value="">Konu seçiniz...</option>
                                <option value="bilgi">Kişisel verilerimin işlenip işlenmediğini öğrenmek istiyorum</option>
                                <option value="duzeltme">Eksik veya yanlış işlenen verilerimin düzeltilmesini istiyorum</option>
                                <option value="silme">Kişisel verilerimin silinmesini / yok edilmesini istiyorum</option>
                                <option value="aktarim">Verilerimin aktarıldığı 3. kişileri öğrenmek istiyorum</option>
                                <option value="itiraz">Otomatik sistemlerle yapılan analize itiraz ediyorum</option>
                                <option value="diger">Diğer</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Açıklama <span className="text-red-500">*</span></label>
                            <textarea
                                name="aciklama"
                                value={formData.aciklama}
                                onChange={handleChange}
                                required
                                rows={5}
                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
                                placeholder="Talebinizi detaylı bir şekilde açıklayınız..."
                            />
                        </div>
                     </div>
                </div>

                {/* SUBMIT AREA */}
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-5 flex gap-3">
                    <AlertCircle className="text-yellow-600 shrink-0" size={24} />
                    <p className="text-sm text-yellow-800 leading-relaxed">
                        <strong>Yasal Uyarı:</strong> Bu form aracılığıyla iletilen bilgiler sadece başvurunuzu değerlendirmek ve sonuçlandırmak amacıyla kullanılacaktır. Şirketimiz, başvurunun güvenliğini sağlamak adına ek kimlik doğrulaması talep etme hakkını saklı tutar.
                    </p>
                </div>

                <div className="flex justify-end pt-4">
                    <Button
                        type="submit"
                        variant="primary"
                        className="w-full md:w-auto px-10 py-4 text-lg font-bold shadow-xl shadow-primary-900/20 hover:shadow-primary-900/30 hover:-translate-y-1 transition-all"
                        icon={<Send size={20} />}
                    >
                        Başvuruyu Gönder
                    </Button>
                </div>

            </form>
        </motion.div>
      </Section>
    </>
  );
};

export default KVKKForm;